/**
 * Small shared UI pieces for the personal Monitor app (`/app/*`):
 * page headers, info cards, error/empty states, and a "coming soon" pill.
 * Keeps the screens consistent and friendly without pulling in the
 * operator-shell component library.
 */

import type { ReactNode } from 'react';

import { c } from './theme';

export function PageHeader({ title, sub }: { title: string; sub: string }) {
  return (
    <div style={{ padding: '20px 18px 4px' }}>
      <h1 style={{ fontSize: 26, fontWeight: 800, margin: '8px 0 4px' }}>{title}</h1>
      <p style={{ fontSize: 14, color: c.inkMuted, margin: '0 0 4px', lineHeight: 1.5 }}>{sub}</p>
    </div>
  );
}

export function ComingPill({ label = 'Coming soon' }: { label?: string }) {
  return (
    <span
      style={{
        fontSize: 11,
        fontWeight: 700,
        color: c.accent,
        background: c.accentSoft,
        border: `1px solid ${c.rim}`,
        borderRadius: 999,
        padding: '3px 10px',
        whiteSpace: 'nowrap',
      }}
    >
      {label}
    </span>
  );
}

export function Card({
  icon,
  title,
  body,
  right,
  tone = 'plain',
}: {
  icon?: ReactNode;
  title: ReactNode;
  body?: ReactNode;
  right?: ReactNode;
  tone?: 'plain' | 'good' | 'warn' | 'accent';
}) {
  const bg =
    tone === 'good'
      ? c.goodSoft
      : tone === 'warn'
        ? c.warnSoft
        : tone === 'accent'
          ? c.accentSoft
          : c.surface;
  return (
    <div
      style={{
        background: bg,
        border: `1px solid ${c.rim}`,
        borderRadius: 16,
        padding: '14px 16px',
        display: 'flex',
        gap: 14,
        alignItems: 'center',
      }}
    >
      {icon && (
        <span
          style={{
            flexShrink: 0,
            width: 44,
            height: 44,
            borderRadius: 12,
            background: c.surface,
            border: `1px solid ${c.rim}`,
            color: c.accent,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {icon}
        </span>
      )}
      <div style={{ minWidth: 0, flex: 1 }}>
        <div style={{ fontSize: 15, fontWeight: 700, color: c.ink }}>{title}</div>
        {body && (
          <div style={{ fontSize: 13, color: c.inkMuted, marginTop: 3, lineHeight: 1.5 }}>{body}</div>
        )}
      </div>
      {right && <div style={{ flexShrink: 0 }}>{right}</div>}
    </div>
  );
}

export function ErrorCard({ children, onRetry }: { children: ReactNode; onRetry: () => void }) {
  return (
    <div
      style={{
        border: `1px solid ${c.dangerRim}`,
        background: c.dangerSoft,
        borderRadius: 14,
        padding: '16px 18px',
        fontSize: 14,
        color: c.danger,
        lineHeight: 1.5,
      }}
    >
      {children}{' '}
      <button
        type="button"
        onClick={onRetry}
        style={{
          fontWeight: 700,
          color: c.danger,
          background: 'none',
          border: 'none',
          padding: 0,
          cursor: 'pointer',
          textDecoration: 'underline',
        }}
      >
        Try again
      </button>
    </div>
  );
}

export function EmptyCard({
  title,
  body,
  actionLabel,
  onAction,
  disabled,
}: {
  title: string;
  body: string;
  actionLabel: string;
  onAction: () => void;
  disabled?: boolean;
}) {
  return (
    <div
      style={{
        border: `1px dashed ${c.rim}`,
        borderRadius: 16,
        padding: '28px 22px',
        textAlign: 'center',
        marginTop: 18,
        background: c.surface,
      }}
    >
      <div style={{ fontSize: 17, fontWeight: 800, color: c.ink, marginBottom: 6 }}>{title}</div>
      <p style={{ fontSize: 14, color: c.inkMuted, margin: '0 0 18px', lineHeight: 1.5 }}>{body}</p>
      <button
        type="button"
        onClick={onAction}
        disabled={disabled}
        style={{
          minHeight: 48,
          padding: '0 24px',
          fontSize: 16,
          fontWeight: 800,
          color: '#ffffff',
          background: disabled ? '#93a4c8' : c.accent,
          border: 'none',
          borderRadius: 12,
          cursor: disabled ? 'default' : 'pointer',
        }}
      >
        {actionLabel}
      </button>
    </div>
  );
}
